import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * AudioContext — single global HTMLAudioElement + state manager.
 *
 * Every audio surface (Manifest Listen, Meditation player, Soundscape,
 * Sleep, future Rowdy content) goes through this provider so:
 *   1. Only ONE clip plays at any time app-wide.
 *   2. Playback survives route changes (persistent mini-player).
 *   3. Rowdy TTS clips, meditation MP3s, and soundscape loops share one
 *      pause / seek / mute control surface.
 *
 * Public API (useAudio):
 *   play({ src, title, subtitle, artwork, kind, meta })
 *   pause() / resume() / toggle() / close()
 *   seek(seconds)  |  skip(deltaSeconds)  |  setRate(rate)
 *   state: { current, playing, position, duration, rate, loading, error }
 */

const Ctx = createContext(null);

const initialState = {
  current: null,     // { src, title, subtitle, artwork, kind, meta, contentId, sourceContext, loop }
  playing: false,
  loading: false,
  position: 0,
  duration: 0,
  rate: 1,
  error: null,
  sleepTimer: null,  // { endsAt: epoch_ms | 'end_of_track', minutes }
};

// Utility: gentle 3-second fade using rAF so users never get startled by a
// hard cut when a sleep timer expires.
const fadeToZero = (audio, ms = 3000) =>
  new Promise((resolve) => {
    const start = performance.now();
    const startVol = audio.volume;
    const step = (t) => {
      const p = Math.min(1, (t - start) / ms);
      audio.volume = startVol * (1 - p);
      if (p < 1) requestAnimationFrame(step);
      else resolve();
    };
    requestAnimationFrame(step);
  });

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [state, setState] = useState(initialState);
  const currentRef = useRef(null);
  const playRef = useRef(null);
  const sleepTimerRef = useRef(null);   // { rafHandle: setTimeout id, endsAt }
  const lastSaveRef = useRef({ t: 0, pos: 0, contentId: null });
  const stateRef = useRef(state);
  stateRef.current = state;

  if (typeof window !== 'undefined' && !audioRef.current) {
    const a = new Audio();
    a.preload = 'metadata';
    a.preservesPitch = true;
    a.volume = 1;
    audioRef.current = a;
    // Debug hook — expose the shared HTMLAudioElement so automated tests
    // (and manual devtools inspection) can assert paused/currentTime/
    // duration/ended without having to intercept `window.Audio` at
    // document_start. Read-only usage from tests only; product code must
    // still go through the useAudio() hook.
    try { window.__rbrAudio = a; } catch { /* ignore */ }
  }

  // Persist listening position — throttled to ~15s and on pause/end.
  const persistPosition = useCallback((force = false) => {
    const cur = currentRef.current;
    const audio = audioRef.current;
    if (!cur?.contentId || !audio) return;
    const now = Date.now();
    const pos = audio.currentTime || 0;
    if (!force && now - lastSaveRef.current.t < 15000 && Math.abs(pos - lastSaveRef.current.pos) < 15) return;
    lastSaveRef.current = { t: now, pos, contentId: cur.contentId };
    const token = localStorage.getItem('rbr_token');
    if (!token) return;
    try {
      // fire-and-forget — never block playback on the write. Use keepalive
      // so page-unload triggers still land.
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/listening/position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          contentId: cur.contentId,
          position: pos,
          duration: audio.duration || 0,
          sourceContext: cur.sourceContext || null,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* never crash on progress writes */ }
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    const onLoaded = () => setState((s) => ({ ...s, duration: a.duration || 0, loading: false }));
    const onTime = () => {
      setState((s) => ({ ...s, position: a.currentTime || 0 }));
      persistPosition(false);
      if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
        try { navigator.mediaSession.setPositionState?.({ duration: a.duration || 0, position: a.currentTime || 0, playbackRate: a.playbackRate || 1 }); } catch {}
      }
    };
    const onPlay   = () => setState((s) => ({ ...s, playing: true, loading: false }));
    const onPause  = () => { setState((s) => ({ ...s, playing: false })); persistPosition(true); };
    const onEnd    = () => {
      persistPosition(true);
      if (typeof window !== 'undefined') {
        const c = currentRef.current;
        window.dispatchEvent(new CustomEvent('rbr:audio-ended', { detail: { contentId: c?.contentId || null, src: c?.src || null } }));
      }
      // "End of track" sleep-timer mode: close the mini-player when the
      // track finishes so no lingering paused player is left on screen.
      if (sleepTimerRef.current?.mode === 'end_of_track') {
        sleepTimerRef.current = null;
        currentRef.current = null;
        setState({ ...initialState });
        return;
      }
      // Playlist auto-advance — only triggers when the *current* track was
      // played with a `meta.playlist` (e.g. sixtySeconds). This never runs
      // on pause / user-initiated stop / manual next — `ended` fires only
      // on natural end of media.
      const cur = currentRef.current;
      const pl = cur?.meta?.playlist;
      const queue = cur?.meta?.queue;
      const idx = cur?.meta?.index;
      if (pl && Array.isArray(queue) && queue.length > 1 && typeof idx === 'number') {
        const loopBack = !!cur?.meta?.loopPlaylist;
        let nextIdx = idx + 1;
        if (nextIdx >= queue.length) {
          if (!loopBack) {
            setState((s) => ({ ...s, playing: false, position: 0 }));
            return;
          }
          nextIdx = 0;
        }
        const nextItem = queue[nextIdx];
        if (nextItem && nextItem.src) {
          // Schedule a fresh play() so React state settles first.
          setTimeout(() => {
            // If the user paused / closed during that microtask, bail.
            const a2 = audioRef.current;
            if (!a2 || currentRef.current !== cur) return;
            playRef.current?.({
              src: nextItem.src,
              title: nextItem.title,
              subtitle: nextItem.subtitle || cur.subtitle,
              artwork: nextItem.artwork || cur.artwork,
              kind: nextItem.kind || 'audio',
              contentId: nextItem.contentId || null,
              sourceContext: cur.sourceContext,
              meta: { playlist: pl, queue, index: nextIdx, loopPlaylist: loopBack },
            });
          }, 0);
          return;
        }
      }
      setState((s) => ({ ...s, playing: false, position: 0 }));
    };
    const onError  = () => setState((s) => ({ ...s, playing: false, loading: false, error: 'Playback failed' }));
    const onWaiting = () => setState((s) => ({ ...s, loading: true }));
    const onPlaying = () => setState((s) => ({ ...s, loading: false }));

    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnd);
    a.addEventListener('error', onError);
    a.addEventListener('waiting', onWaiting);
    a.addEventListener('playing', onPlaying);

    // Save on page-hide (mobile close-tab, safari suspend, etc.)
    const onBeforeUnload = () => persistPosition(true);
    window.addEventListener('pagehide', onBeforeUnload);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('error', onError);
      a.removeEventListener('waiting', onWaiting);
      a.removeEventListener('playing', onPlaying);
      window.removeEventListener('pagehide', onBeforeUnload);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [persistPosition]);

  // Cross-media mutual exclusion — pause the AudioContext when a different
  // media source (e.g. the VideoModal <video>) announces it's starting.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOtherStart = (e) => {
      const src = e?.detail?.source;
      const a = audioRef.current;
      if (!a || src === 'audio') return;
      try { a.pause(); } catch { /* ignore */ }
    };
    window.addEventListener('rbr:media-start', onOtherStart);
    return () => window.removeEventListener('rbr:media-start', onOtherStart);
  }, []);

  const play = useCallback(async ({ src, title, subtitle, artwork, kind, meta, contentId, sourceContext, loop } = {}) => {
    const a = audioRef.current;
    if (!a || !src) return;
    // Global mutual exclusion — signal any other <video>/<audio> that we are
    // taking the "playing" slot so they pause themselves.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('rbr:media-start', { detail: { source: 'audio', src } }));
    }
    const sameSrc = currentRef.current?.src === src;
    const track = { src, title, subtitle, artwork, kind: kind || 'audio', meta: meta || null, contentId: contentId || null, sourceContext: sourceContext || null, loop: !!loop };
    currentRef.current = track;
    a.loop = !!loop;
    a.volume = 1;
    if (!sameSrc) {
      try {
        a.src = src;
        a.load();
      } catch {}
      setState((s) => ({ ...s, current: track, position: 0, duration: 0, error: null, loading: true }));
    } else {
      setState((s) => ({ ...s, current: track }));
    }
    // MediaSession — real lock-screen controls where the platform supports it.
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: title || 'Reflections by Rowdy',
          artist: subtitle || 'Rowdy Solomon',
          album: 'Reflections by Rowdy',
          artwork: artwork ? [{ src: artwork, sizes: '512x512', type: 'image/png' }] : [],
        });
        navigator.mediaSession.setActionHandler('play',  () => a.play().catch(() => {}));
        navigator.mediaSession.setActionHandler('pause', () => a.pause());
        navigator.mediaSession.setActionHandler('seekbackward', () => { a.currentTime = Math.max(0, (a.currentTime || 0) - 15); });
        navigator.mediaSession.setActionHandler('seekforward',  () => { a.currentTime = Math.min(a.duration || 0, (a.currentTime || 0) + 15); });
        navigator.mediaSession.setActionHandler('seekto', (e) => { if (e.seekTime != null) a.currentTime = e.seekTime; });
      } catch { /* not supported everywhere */ }
    }
    try {
      await a.play();
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setState((s) => ({ ...s, playing: false, loading: false, error: 'Tap play again to start' }));
      }
    }
  }, []);
  // Keep a ref so onEnd's auto-advance can call play() without a hoist issue.
  playRef.current = play;

  const pause = useCallback(() => {
    const a = audioRef.current;
    if (a && !a.paused) a.pause();
  }, []);

  const resume = useCallback(async () => {
    const a = audioRef.current;
    if (!a || !currentRef.current) return;
    try { await a.play(); } catch { /* user gesture required */ }
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !a.src) return;
    if (a.paused) resume();
    else pause();
  }, [pause, resume]);

  const seek = useCallback((seconds) => {
    const a = audioRef.current;
    if (!a) return;
    const clamped = Math.max(0, Math.min(a.duration || 0, seconds));
    a.currentTime = clamped;
    setState((s) => ({ ...s, position: clamped }));
  }, []);

  const skip = useCallback((delta) => {
    const a = audioRef.current;
    if (!a) return;
    seek((a.currentTime || 0) + delta);
  }, [seek]);

  const setRate = useCallback((rate) => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = rate;
    setState((s) => ({ ...s, rate }));
  }, []);

  const close = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try { a.pause(); } catch { /* ignore */ }
      a.removeAttribute('src');
      try { a.load(); } catch { /* ignore */ }
    }
    currentRef.current = null;
    if (sleepTimerRef.current) { clearTimeout(sleepTimerRef.current.handle); sleepTimerRef.current = null; }
    setState(initialState);
  }, []);

  // Sleep timer — minutes OR the string 'end_of_track'. `null` cancels.
  const setSleepTimer = useCallback((value) => {
    // Cancel any prior timer first.
    if (sleepTimerRef.current) { clearTimeout(sleepTimerRef.current.handle); sleepTimerRef.current = null; }
    if (value == null) { setState((s) => ({ ...s, sleepTimer: null })); return; }
    if (value === 'end_of_track') {
      sleepTimerRef.current = { mode: 'end_of_track' };
      setState((s) => ({ ...s, sleepTimer: { endsAt: 'end_of_track', minutes: null } }));
      return;
    }
    const minutes = Number(value);
    if (!Number.isFinite(minutes) || minutes <= 0) return;
    const endsAt = Date.now() + minutes * 60_000;
    const handle = setTimeout(async () => {
      const a = audioRef.current;
      if (a && !a.paused) {
        await fadeToZero(a, 3000);
        try { a.pause(); } catch {}
      }
      // Persist final position before we tear down so Continue Listening
      // (or the mood/progress dashboard) still knows how far the user got.
      persistPosition(true);
      // Sleep-timer specific: close the mini-player entirely for a peaceful
      // dark exit. For soundscapes we always close; for spoken content we
      // also close because the user explicitly asked for the audio to end.
      // Restore the volume so the next play() session doesn't start silent.
      if (a) { try { a.volume = 1; } catch {} }
      currentRef.current = null;
      sleepTimerRef.current = null;
      setState({ ...initialState });
    }, minutes * 60_000);
    sleepTimerRef.current = { handle, endsAt };
    setState((s) => ({ ...s, sleepTimer: { endsAt, minutes } }));
  }, [persistPosition]);

  const value = useMemo(() => ({
    state,
    play, pause, resume, toggle, seek, skip, setRate, close, setSleepTimer,
  }), [state, play, pause, resume, toggle, seek, skip, setRate, close, setSleepTimer]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAudio = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAudio must be used inside <AudioProvider>');
  return ctx;
};
